import classNames from 'classnames';
import React, { Component, createRef, KeyboardEvent, ReactElement, RefObject } from 'react';
import { Dictionary } from 'scripts/util/constants/i18n.constants';
import { DropdownButton } from './dropdown-button';
import { DropdownList } from './dropdown-list';
import { IDropdownOption } from './dropdown.interfaces';

let uniqueId = 0;

export interface IDropdownProps {
  className?: string;
  dictionary?: string;
  id?: string;
  label?: string;
  labelSelected?: string;
  nav?: boolean;
  onSelect: (option: IDropdownOption) => void;
  options: IDropdownOption[];
  selected?: IDropdownOption;
  toggleClass?: string;
}

interface IDropdownState {
  show: boolean;
}

export class Dropdown extends Component<IDropdownProps, IDropdownState> {
  private id: number;
  private dropdownListRef: RefObject<HTMLUListElement>;
  private dropdownButtonRef: RefObject<HTMLButtonElement>;
  private firstDropdownOptionRef: RefObject<HTMLLIElement | HTMLAnchorElement>;

  constructor(props: IDropdownProps) {
    super(props);
    this.id = uniqueId++;
    this.state = { show: false };
    this.dropdownListRef = createRef<HTMLUListElement>();
    this.dropdownButtonRef = createRef<HTMLButtonElement>();
    this.firstDropdownOptionRef = createRef<HTMLLIElement | HTMLAnchorElement>();
  }

  public componentDidMount(): void {
    document.addEventListener('mousedown', this.handleMouseDown);
  }

  public componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleMouseDown);
  }

  public render(): ReactElement<IDropdownProps> {
    const {
      className,
      dictionary = Dictionary.COMMON,
      id,
      label,
      labelSelected,
      nav,
      options,
      selected,
      toggleClass,
    } = this.props;
    const { show } = this.state;
    const optionsId = `dropdown-options-${this.id}`;
    const toggleId = `dropdown-toggle-${this.id}`;
    return (
      <div
        className={classNames('dropdown', { 'text-only': nav }, className)}
        data-testid="dropdown"
        id={id}
        onKeyDown={this.handleKeyDown}
      >
        <DropdownButton
          dictionary={dictionary}
          label={nav ? label : selected.label}
          labelSelected={labelSelected}
          onClick={this.handleButtonClick}
          optionsId={optionsId}
          ref={this.dropdownButtonRef}
          show={show}
          toggleClass={toggleClass}
          toggleId={toggleId}
        />
        {show && (
          <DropdownList
            dictionary={dictionary}
            firstOptionRef={this.firstDropdownOptionRef}
            nav={nav}
            onSelect={this.handleListClick}
            options={options}
            optionsId={optionsId}
            selected={selected}
            toggleId={toggleId}
            ref={this.dropdownListRef}
            topOffset={this.getDropdownListTopOffset()}
          />
        )}
      </div>
    );
  }

  private focusOnDropdownButton(): void {
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.focus();
    }
  }

  private focusOnFirstDropdownOption(): void {
    if (this.firstDropdownOptionRef.current) {
      this.firstDropdownOptionRef.current.focus();
    }
  }

  private handleButtonClick = (): void => {
    this.toggleShow();
  };

  private handleListClick = (option: IDropdownOption): void => {
    const { onSelect } = this.props;
    onSelect(option);
    this.toggleShow();
  };

  private handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const { show } = this.state;
    switch (event.keyCode) {
      case 40: // down arrow
        if (!show) {
          this.toggleShow();
          this.focusOnFirstDropdownOption();
        } else if (event.target === this.dropdownButtonRef.current) {
          this.focusOnFirstDropdownOption();
        }
        event.preventDefault();
        break;
      case 38: // up arrow
        if (event.target === this.firstDropdownOptionRef.current) {
          this.focusOnDropdownButton();
        } else if (show && event.target === this.dropdownButtonRef.current) {
          this.toggleShow();
          this.focusOnDropdownButton();
        }
        event.preventDefault();
        break;
      case 13: // return
      case 32: // space
        if (event.target !== this.dropdownButtonRef.current) {
          (event.target as HTMLElement).click();
          this.focusOnDropdownButton();
        } else {
          this.toggleShow();
          this.focusOnDropdownButton();
        }
        event.preventDefault();
        break;
      case 27: // escape
        if (show) {
          this.toggleShow();
        }
        this.focusOnDropdownButton();
        event.preventDefault();
        break;
    }
  };

  private handleMouseDown = (event: MouseEvent): void => {
    const { show } = this.state;
    if (!show) {
      return;
    }
    const clickedOnDropdown =
      this.dropdownListRef.current && this.dropdownListRef.current.contains(event.target as HTMLElement);
    const clickedOnButton =
      this.dropdownButtonRef.current && this.dropdownButtonRef.current.contains(event.target as HTMLElement);
    if (!clickedOnDropdown && !clickedOnButton) {
      this.toggleShow();
    }
  };

  private toggleShow(): void {
    const { show } = this.state;
    this.setState({ show: !show });
  }

  private getDropdownListTopOffset(): number | undefined {
    if (!this.dropdownButtonRef.current) {
      return undefined;
    }
    return this.dropdownButtonRef.current.offsetHeight + this.dropdownButtonRef.current.offsetTop;
  }
}
